import React, { FC, useMemo, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import _sortBy from 'lodash/sortBy';
import ErrorMessage from '@minecraft.error-message';
import { useDrawerSearchFilterNameOrCategory, useDrawerFiltersCounts, useDrawerGlobalLoader } from '@minecraft.drawer';
import {
  FormItem,
  useFiltersLabel,
  generateSelectOptionsBasedOnField,
  FiltersNameEnum,
  FilterDrawerType,
} from '@blocs.filter';
import { FiltersCategoryNameEnum, FiltersCountNameEnum } from '../../shared/types';
import { Tattoos, TattoosNote, Piercings, PiercingsNote } from '../../FilterDrawerCollection';
import { QUERY_TATTOOS_AND_PIERCINGS } from './TattoosAndPiercingsCategoryGraphQL';

interface Props {
  type: FilterDrawerType;
}

const TattoosAndPiercingsCategory: FC<Props> = ({ type }) => {
  const filtersLabelOfAudience = useFiltersLabel(type);

  const { data, error, loading } = useQuery(QUERY_TATTOOS_AND_PIERCINGS);

  const { isCategoryMatchedSearch, shouldCategoryFilterBeVisible, assignOptionsToCategoryFilters } =
    useDrawerSearchFilterNameOrCategory(FiltersCategoryNameEnum.tattoosAndPiercings);
  const { filtersCounts } = useDrawerFiltersCounts();

  const { tattooOptions, piercingOptions } = useMemo(() => {
    return {
      tattooOptions: generateSelectOptionsBasedOnField(
        _sortBy(data?.tattoos, 'name'),
        'name',
        false,
        filtersCounts?.[FiltersCountNameEnum.tattooIds]
      ),
      piercingOptions: generateSelectOptionsBasedOnField(
        _sortBy(data?.piercings, 'name'),
        'name',
        false,
        filtersCounts?.[FiltersCountNameEnum.piercingIds]
      ),
    };
  }, [data, filtersCounts]);

  useDrawerGlobalLoader(loading);

  useEffect(() => {
    if (tattooOptions.length > 0) {
      assignOptionsToCategoryFilters({
        [FiltersNameEnum.tattoosList]: tattooOptions,
        [FiltersNameEnum.piercingsList]: piercingOptions,
      });
    }
  }, [tattooOptions]);

  if (error) {
    return <ErrorMessage error={error} />;
  }

  return (
    isCategoryMatchedSearch && (
      <>
        {shouldCategoryFilterBeVisible(FiltersNameEnum.tattoosList) && (
          <FormItem $fullWidth $marginValue={2}>
            <Tattoos name={FiltersNameEnum.tattoosList} label={filtersLabelOfAudience[FiltersNameEnum.tattoosList]} />
          </FormItem>
        )}
        {shouldCategoryFilterBeVisible(FiltersNameEnum.tattoosNote) && (
          <FormItem $fullWidth $marginValue={2}>
            <TattoosNote
              name={FiltersNameEnum.tattoosNote}
              label={filtersLabelOfAudience[FiltersNameEnum.tattoosNote]}
            />
          </FormItem>
        )}
        {shouldCategoryFilterBeVisible(FiltersNameEnum.piercingsList) && (
          <FormItem $fullWidth $marginValue={2}>
            <Piercings
              name={FiltersNameEnum.piercingsList}
              label={filtersLabelOfAudience[FiltersNameEnum.piercingsList]}
            />
          </FormItem>
        )}
        {shouldCategoryFilterBeVisible(FiltersNameEnum.piercingsNote) && (
          <FormItem $fullWidth $marginValue={2}>
            <PiercingsNote
              name={FiltersNameEnum.piercingsNote}
              label={filtersLabelOfAudience[FiltersNameEnum.piercingsNote]}
            />
          </FormItem>
        )}
      </>
    )
  );
};

export default TattoosAndPiercingsCategory;
